<template>
  <div :id="data.id" class="bg-gray-lighten3 pb-10 md:pb-14">
    <div class="container">
      <div v-if="data && data.items">
        <swiper-container ref="swiperEl" init="false" class="pb-9">
          <div slot="container-end" class="swiper-scrollbar"></div>

          <swiper-slide
            v-for="(item, i) in data.items"
            :key="i"
            class="relative flex w-full max-w-full flex-col overflow-hidden md:w-[890px]"
          >
            <div
              v-lazy-background="getAssetUrl(item.image)"
              class="relative w-full bg-cover bg-center bg-no-repeat pt-[85.7%] md:mb-0 md:h-[800px] md:p-0"
            >
              <div
                v-if="!isDesktop && item.tags"
                class="absolute inset-0 flex flex-col justify-end p-6"
              >
                <div
                  class="title flex flex-wrap-reverse gap-2 text-xs uppercase leading-none tracking-[0.12em] text-white md:tracking-normal"
                >
                  <div
                    v-for="(tag, t) in [...item.tags].reverse()"
                    :key="t"
                    class="tag flex h-[48px] min-w-[120px] items-center rounded-lg bg-cover px-2 backdrop-blur-xl md:px-4"
                  >
                    <object
                      v-if="tag.image"
                      :data="getImage(tag.image)"
                      type="image/svg+xml"
                      class="mr-2 h-8 w-8"
                    ></object>

                    <span class="whitespace-pre-line" v-html="tag.text" />
                  </div>
                </div>
              </div>
            </div>

            <div
              class="z-10 flex flex-grow flex-col rounded-b-lg bg-white p-6 md:absolute md:inset-0 md:z-[1] md:flex md:flex-col md:justify-end md:bg-transparent md:p-16 md:text-white"
            >
              <UiLink
                :to="item.link"
                target="_blank"
                class="title mb-2 inline-flex text-3xl uppercase leading-none text-slate md:mt-auto md:text-[3.5rem] md:text-white"
              >
                <span v-html="item.title" />
              </UiLink>

              <div
                class="mb-6 font-segoe text-sm leading-[1.25rem] text-slate-lighten1 md:max-w-[550px] md:text-md md:leading-[2.25rem] md:text-white"
                v-html="item.text"
              />

              <div
                v-if="item.sizes"
                class="title flex gap-2 text-lg uppercase leading-none text-slate md:mb-8 md:gap-4 md:text-xl"
              >
                <UiLink
                  v-for="(size, s) in item.sizes"
                  :key="s"
                  :to="size.link"
                  class="size flex h-11 min-w-[60px] items-center justify-center rounded-lg bg-gray-lighten3 bg-center bg-no-repeat md:h-[68px] md:w-[96px] md:bg-transparent md:bg-white md:p-[13.6px]"
                >
                  <span v-html="size.text" />
                </UiLink>
              </div>

              <div
                v-if="isDesktop && item.tags && item.tags.length"
                class="title flex gap-3 text-xs uppercase leading-none tracking-[0.12em] text-slate-lighten1 md:text-sm md:tracking-normal md:text-white"
              >
                <div
                  v-for="(tag, t) in item.tags"
                  :key="t"
                  class="tag flex h-[38px] min-w-[80px] items-center rounded-lg border-[1px] border-gray bg-cover px-2 backdrop-blur-xl md:h-[64px] md:min-w-[160px] md:border-transparent md:px-4"
                >
                  <object
                    v-if="tag.image"
                    :data="getImage(tag.image)"
                    type="image/svg+xml"
                    class="mr-4 h-8 w-8"
                  ></object>

                  <span class="md:whitespace-pre-line" v-html="tag.text" />
                </div>
              </div>
            </div>
          </swiper-slide>
        </swiper-container>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useBreakpoint, useMediaAsset, useSwiperElement } from '~/composables';

interface Props {
  data?: { id?: string; items: any[] };
}

withDefaults(defineProps<Props>(), {
  data: () => ({ items: [] }),
});

const { getAssetUrl } = useMediaAsset();
const { isBiggerThanBreakpoint: isDesktop } = useBreakpoint('md');

const swiperOptions = {
  observer: true,
  slidesPerView: 'auto',
  spaceBetween: 16,
  scrollbar: {
    el: '.swiper-scrollbar',
    draggable: true,
    dragSize: 180,
    snapOnRelease: true,
  },
};

const swiperEl = useSwiperElement(swiperOptions);

const getImage = (src: string) => getAssetUrl(src) ?? undefined;
</script>

<style lang="sass" scoped>
.tag
  @media (min-width: 768px)
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.24) 0%, rgba(255, 255, 255, 0.16) 100%)

.size
  @media (min-width: 768px)
    background-image: url('/images/product-size-bg-big.svg')
</style>
